import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Space, Tag, Row, Col, message,
  Tooltip, Upload, Tabs, Badge, Statistic
} from 'antd';
import {
  PlusOutlined, UploadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FileTextOutlined, DollarOutlined, WarningOutlined, SyncOutlined,
  EyeOutlined, ClockCircleOutlined, CalendarOutlined, CreditCardOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService, clientVendorService, purchaseOrderService } from '../services/api';
import dayjs from 'dayjs';
import '../styles/Phase5.css';

const { Option } = Select;
const { TextArea } = Input;

const Invoices = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [agingReport, setAgingReport] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [form] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractedLineItems, setExtractedLineItems] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesData, statsData, agingData, vendorsData, posData] = await Promise.all([
        invoiceService.getAll(),
        invoiceService.getStats(),
        invoiceService.getAgingReport(),
        clientVendorService.getAll(),
        purchaseOrderService.getAll({ status: 'approved' }),
      ]);
      setInvoices(invoicesData);
      setStats(statsData);
      setAgingReport(agingData);
      setVendors(vendorsData);
      setPurchaseOrders(posData);
    } catch (error) {
      message.error('Failed to load invoices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAddModal = () => {
    setLineItems([{ key: 1, description: '', quantity: 1, unit_price: 0 }]);
    form.resetFields();
    form.setFieldsValue({ invoice_date: dayjs(), currency: 'USD' });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setLineItems([]);
    form.resetFields();
  };

  const calculateTotals = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  };

  const handleSubmit = async (values) => {
    try {
      const invoiceData = {
        ...values,
        invoice_date: values.invoice_date?.toISOString(),
        due_date: values.due_date?.toISOString(),
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          po_line_item_id: item.po_line_item_id,
        })),
      };

      await invoiceService.create(invoiceData);
      message.success('Invoice created successfully');
      handleCancel();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setExtracting(true);
    setExtractedData(null);
    setExtractedLineItems([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await invoiceService.extractMetadata(formData);
      setExtractedData(result);

      // Set line items from extraction
      if (result.line_items && result.line_items.length > 0) {
        setExtractedLineItems(result.line_items.map((item, idx) => ({
          key: idx + 1,
          description: item.description,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || item.amount,
        })));
      }

      // Populate form with extracted data
      const formValues = {
        invoice_number: result.invoice_number,
        invoice_date: result.invoice_date ? dayjs(result.invoice_date) : null,
        due_date: result.due_date ? dayjs(result.due_date) : null,
        total_amount: result.total_amount,
        subtotal: result.subtotal,
        tax_amount: result.tax_amount,
        notes: result.notes || result.payment_instructions,
      };

      // Try to match vendor
      if (result.vendor_matches && result.vendor_matches.length > 0) {
        formValues.client_vendor_id = result.vendor_matches[0].client_vendor_id;
      }

      // Try to match PO
      if (result.po_matches && result.po_matches.length > 0) {
        formValues.purchase_order_id = result.po_matches[0].id;
      }

      uploadForm.setFieldsValue(formValues);
      message.success(`Extracted invoice data with ${Math.round(result.extraction_confidence * 100)}% confidence`);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to extract invoice data');
      console.error('Extraction error:', error);
    } finally {
      setExtracting(false);
    }

    return false; // Prevent default upload
  };

  const handleUploadSubmit = async (values) => {
    try {
      if (!selectedFile) {
        message.error('Please select a file');
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('invoice_number', values.invoice_number);
      if (values.vendor_id) formData.append('vendor_id', values.vendor_id);
      formData.append('client_vendor_id', values.client_vendor_id || '');
      formData.append('invoice_date', values.invoice_date?.toISOString());
      if (values.due_date) formData.append('due_date', values.due_date?.toISOString());
      formData.append('total_amount', values.total_amount);
      if (values.purchase_order_id) formData.append('purchase_order_id', values.purchase_order_id);
      if (values.notes) formData.append('notes', values.notes);

      await invoiceService.upload(formData);
      message.success('Invoice uploaded successfully');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      setSelectedFile(null);
      setExtractedData(null);
      setExtractedLineItems([]);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to upload invoice');
    }
  };

  const handleApprove = async (id) => {
    try {
      await invoiceService.approve(id);
      message.success('Invoice approved');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const handlePerformMatch = async (id) => {
    try {
      const result = await invoiceService.performMatch(id);
      if (result.match_status === 'matched') {
        message.success('Invoice matched successfully');
      } else {
        message.warning(`Match found ${result.discrepancies?.length || 0} discrepancies`);
      }
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to perform match');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      key: lineItems.length + 1,
      description: '',
      quantity: 1,
      unit_price: 0,
    }]);
  };

  const removeLineItem = (key) => {
    setLineItems(lineItems.filter(item => item.key !== key));
  };

  const updateLineItem = (key, field, value) => {
    setLineItems(lineItems.map(item =>
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  const getStatusTagClass = (status) => {
    const classes = {
      received: 'p5-tag--info',
      under_review: 'p5-tag--warning',
      approved: 'p5-tag--success',
      rejected: 'p5-tag--danger',
      paid: 'p5-tag--success',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const getMatchStatusTagClass = (status) => {
    const classes = {
      pending: 'p5-tag--neutral',
      matched: 'p5-tag--success',
      exception: 'p5-tag--danger',
      approved: 'p5-tag--success',
      rejected: 'p5-tag--danger',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const getPaymentStatusTagClass = (status) => {
    const classes = {
      unpaid: 'p5-tag--danger',
      partial: 'p5-tag--warning',
      paid: 'p5-tag--success',
    };
    return classes[status] || 'p5-tag--neutral';
  };

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text, record) => (
        <button
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--p5-font-mono)',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--p5-navy-800)',
            padding: 0,
          }}
          onClick={() => navigate(`/invoices/${record.id}`)}
        >
          {text}
        </button>
      ),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => {
        const vendorName = record.vendor?.name ||
          record.client_vendor?.vendor?.name ||
          'N/A';
        return <span className="p5-table-primary">{vendorName}</span>;
      },
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (date) => (
        <span className="p5-table-secondary">
          {date ? dayjs(date).format('MMM D, YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date, record) => {
        if (!date) return <span className="p5-text-slate">-</span>;
        const dueDate = dayjs(date);
        const isOverdue = dueDate.isBefore(dayjs()) && record.payment_status !== 'paid';
        return (
          <span style={{
            color: isOverdue ? 'var(--p5-rose-500)' : 'var(--p5-navy-700)',
            fontWeight: isOverdue ? 600 : 400
          }}>
            {dueDate.format('MMM D, YYYY')}
            {isOverdue && <WarningOutlined style={{ marginLeft: 6, fontSize: '12px' }} />}
          </span>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount, record) => (
        <span className="p5-font-mono p5-table-primary">
          {record.currency || 'USD'} {amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Balance Due',
      dataIndex: 'balance_due',
      key: 'balance_due',
      render: (amount) => (
        <span className="p5-font-mono" style={{
          color: amount > 0 ? 'var(--p5-rose-500)' : 'var(--p5-emerald-500)',
          fontWeight: 600
        }}>
          ${amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`p5-tag ${getStatusTagClass(status)}`}>
          <span className="p5-tag-dot" />
          {status?.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Match',
      dataIndex: 'match_status',
      key: 'match_status',
      render: (status) => (
        <span className={`p5-tag ${getMatchStatusTagClass(status)}`}>
          {status?.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status) => (
        <span className={`p5-tag ${getPaymentStatusTagClass(status)}`}>
          {status?.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/invoices/${record.id}`)}
              style={{ color: 'var(--p5-slate-400)' }}
            />
          </Tooltip>
          {record.purchase_order_id && record.match_status === 'pending' && (
            <Tooltip title="Run 3-Way Match">
              <Button
                type="text"
                icon={<SyncOutlined />}
                size="small"
                onClick={() => handlePerformMatch(record.id)}
                style={{ color: 'var(--p5-amber-500)' }}
              />
            </Tooltip>
          )}
          {record.status !== 'approved' && record.status !== 'paid' && (
            <Tooltip title="Approve">
              <Button
                icon={<CheckCircleOutlined />}
                size="small"
                className="p5-btn p5-btn--accent"
                onClick={() => handleApprove(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const filteredInvoices = invoices.filter(inv => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return inv.status === 'received' || inv.status === 'under_review';
    if (activeTab === 'approved') return inv.status === 'approved';
    if (activeTab === 'exceptions') return inv.match_status === 'exception';
    if (activeTab === 'overdue') {
      return inv.due_date && dayjs(inv.due_date).isBefore(dayjs()) && inv.payment_status !== 'paid';
    }
    return true;
  });

  const pendingCount = invoices.filter(i => i.status === 'received' || i.status === 'under_review').length;
  const exceptionCount = invoices.filter(i => i.match_status === 'exception').length;
  const overdueCount = invoices.filter(i => i.due_date && dayjs(i.due_date).isBefore(dayjs()) && i.payment_status !== 'paid').length;

  const tabItems = [
    { key: 'all', label: 'All Invoices' },
    {
      key: 'pending',
      label: (
        <Badge count={pendingCount} offset={[10, 0]}>
          <span>Pending Review</span>
        </Badge>
      ),
    },
    { key: 'approved', label: 'Approved' },
    {
      key: 'exceptions',
      label: (
        <Badge count={exceptionCount} offset={[10, 0]} style={{ backgroundColor: 'var(--p5-rose-500)' }}>
          <span>Exceptions</span>
        </Badge>
      ),
    },
    {
      key: 'overdue',
      label: (
        <Badge count={overdueCount} offset={[10, 0]} style={{ backgroundColor: 'var(--p5-amber-500)' }}>
          <span>Overdue</span>
        </Badge>
      ),
    },
  ];

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Invoices</h1>
          <p className="p5-page-subtitle">
            Manage vendor invoices, 3-way matching, and accounts payable
          </p>
        </div>
        <Space>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            Upload Invoice
          </Button>
          <Button
            className="p5-btn p5-btn--accent"
            icon={<PlusOutlined />}
            onClick={showAddModal}
          >
            Create Invoice
          </Button>
        </Space>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <FileTextOutlined />
            </div>
            <div className="p5-stat-label">Total Invoices</div>
            <div className="p5-stat-value">{stats.total_invoices}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <DollarOutlined />
            </div>
            <div className="p5-stat-label">Total Amount</div>
            <div className="p5-stat-value p5-stat-value--currency">
              {stats.total_amount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--violet">
              <CreditCardOutlined />
            </div>
            <div className="p5-stat-label">Amount Paid</div>
            <div className="p5-stat-value" style={{ color: 'var(--p5-emerald-500)' }}>
              ${stats.amount_paid?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--rose">
              <ExclamationCircleOutlined />
            </div>
            <div className="p5-stat-label">Amount Due</div>
            <div className="p5-stat-value" style={{
              color: stats.amount_due > 0 ? 'var(--p5-rose-500)' : 'var(--p5-emerald-500)'
            }}>
              ${stats.amount_due?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--sky">
              <WarningOutlined />
            </div>
            <div className="p5-stat-label">Overdue</div>
            <div className="p5-stat-value" style={{
              color: stats.overdue_amount > 0 ? 'var(--p5-rose-500)' : 'var(--p5-emerald-500)'
            }}>
              ${stats.overdue_amount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      )}

      {/* Aging Summary */}
      {agingReport && agingReport.total_amount > 0 && (
        <div className="p5-card p5-animate-fadeUp" style={{ marginBottom: '32px', animationDelay: '200ms' }}>
          <div className="p5-card-body">
            <h3 className="p5-section-title">
              <CalendarOutlined /> AP Aging Summary
            </h3>
            <Row gutter={24}>
              {agingReport.buckets.map(bucket => (
                <Col span={4} key={bucket.bucket}>
                  <div style={{
                    textAlign: 'center',
                    padding: '16px',
                    background: 'var(--p5-slate-100)',
                    borderRadius: 'var(--p5-radius-md)',
                  }}>
                    <div className="p5-label" style={{ marginBottom: '8px' }}>
                      {bucket.bucket === 'current' ? 'Current' : `${bucket.bucket} Days`}
                    </div>
                    <div className="p5-font-display" style={{
                      fontSize: '1.5rem',
                      color: bucket.bucket === '90+' ? 'var(--p5-rose-500)' :
                             bucket.bucket === '61-90' ? 'var(--p5-amber-500)' : 'var(--p5-navy-800)'
                    }}>
                      ${bucket.total_amount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div className="p5-text-slate" style={{ fontSize: '0.8125rem', marginTop: '4px' }}>
                      {bucket.invoice_count} invoices
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      )}

      {/* Invoice Table */}
      <div className="p5-card p5-animate-fadeUp" style={{ animationDelay: '300ms', padding: 0 }}>
        <div className="p5-flex-between" style={{ padding: '20px 24px', borderBottom: '1px solid var(--p5-slate-100)' }}>
          <h3 className="p5-section-title" style={{ margin: 0 }}>
            <FileTextOutlined /> Invoice List
          </h3>
        </div>
        <div style={{ padding: '0 24px' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="p5-tabs"
          />
        </div>
        <div className="p5-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
          <Table
            columns={columns}
            dataSource={filteredInvoices}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      {/* Create Invoice Modal */}
      <Modal
        title={null}
        open={isModalVisible}
        onCancel={handleCancel}
        width={900}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Create Invoice
          </h2>
        </div>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="invoice_number" label={<span className="p5-label">Invoice Number</span>} rules={[{ required: true }]}>
                <Input placeholder="INV-2024-001" size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="client_vendor_id" label={<span className="p5-label">Vendor</span>} rules={[{ required: true }]}>
                <Select placeholder="Select vendor" showSearch optionFilterProp="children" size="large">
                  {vendors.map(v => (
                    <Option key={v.id} value={v.id}>{v.vendor?.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="purchase_order_id" label={<span className="p5-label">Link to PO</span>}>
                <Select placeholder="Select PO (optional)" allowClear showSearch optionFilterProp="children" size="large">
                  {purchaseOrders.map(po => (
                    <Option key={po.id} value={po.id}>{po.po_number}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="invoice_date" label={<span className="p5-label">Invoice Date</span>} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="due_date" label={<span className="p5-label">Due Date</span>}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="payment_terms" label={<span className="p5-label">Payment Terms</span>}>
                <Select placeholder="Select" size="large">
                  <Option value="Net 30">Net 30</Option>
                  <Option value="Net 60">Net 60</Option>
                  <Option value="Net 90">Net 90</Option>
                  <Option value="Due on Receipt">Due on Receipt</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="currency" label={<span className="p5-label">Currency</span>}>
                <Select size="large">
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            marginTop: '24px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid var(--p5-slate-200)'
          }}>
            <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)', fontSize: '0.9375rem' }}>Line Items</span>
          </div>

          {lineItems.map((item) => (
            <Row gutter={8} key={item.key} style={{ marginBottom: 12 }}>
              <Col span={10}>
                <Input
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateLineItem(item.key, 'description', e.target.value)}
                  size="large"
                />
              </Col>
              <Col span={4}>
                <InputNumber
                  placeholder="Qty"
                  min={1}
                  value={item.quantity}
                  onChange={(val) => updateLineItem(item.key, 'quantity', val)}
                  style={{ width: '100%' }}
                  size="large"
                />
              </Col>
              <Col span={5}>
                <InputNumber
                  placeholder="Unit Price"
                  min={0}
                  precision={2}
                  value={item.unit_price}
                  onChange={(val) => updateLineItem(item.key, 'unit_price', val)}
                  style={{ width: '100%' }}
                  size="large"
                  prefix="$"
                />
              </Col>
              <Col span={4}>
                <Input
                  disabled
                  value={`$${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}`}
                  size="large"
                  style={{ fontFamily: 'var(--p5-font-mono)', fontWeight: 600 }}
                />
              </Col>
              <Col span={1}>
                <Button
                  icon={<CloseCircleOutlined />}
                  danger
                  onClick={() => removeLineItem(item.key)}
                  disabled={lineItems.length === 1}
                  size="large"
                />
              </Col>
            </Row>
          ))}

          <Button
            type="dashed"
            onClick={addLineItem}
            icon={<PlusOutlined />}
            style={{ marginBottom: 24 }}
          >
            Add Line Item
          </Button>

          <Row justify="end" style={{ marginBottom: 24 }}>
            <div style={{
              background: 'var(--p5-navy-900)',
              padding: '16px 24px',
              borderRadius: 'var(--p5-radius-md)'
            }}>
              <span className="p5-label" style={{ color: 'var(--p5-slate-300)', marginRight: '16px' }}>Total</span>
              <span className="p5-font-display" style={{ fontSize: '1.75rem', color: 'var(--p5-amber-400)' }}>
                ${calculateTotals().toFixed(2)}
              </span>
            </div>
          </Row>

          <Form.Item name="notes" label={<span className="p5-label">Notes</span>}>
            <TextArea rows={2} size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={handleCancel}>Cancel</Button>
            <Button className="p5-btn p5-btn--accent" htmlType="submit">Create Invoice</Button>
          </Space>
        </Form>
      </Modal>

      {/* Upload Invoice Modal */}
      <Modal
        title={null}
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadForm.resetFields();
          setSelectedFile(null);
        }}
        footer={null}
        width={600}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Upload Invoice
          </h2>
        </div>
        <Form form={uploadForm} layout="vertical" onFinish={handleUploadSubmit}>
          <Form.Item label={<span className="p5-label">Invoice File</span>} required>
            <Upload
              beforeUpload={handleFileSelect}
              onRemove={() => {
                setSelectedFile(null);
                setExtractedData(null);
                setExtractedLineItems([]);
                uploadForm.resetFields();
              }}
              fileList={selectedFile ? [selectedFile] : []}
              accept=".pdf,.png,.jpg,.jpeg,.webp"
            >
              <Button icon={<UploadOutlined />} size="large" loading={extracting}>
                {extracting ? 'Extracting with AI...' : 'Select File (AI will extract fields)'}
              </Button>
            </Upload>
            {extractedData && (
              <div style={{ marginTop: 8 }}>
                <Tag color={extractedData.extraction_confidence > 0.7 ? 'green' : 'orange'}>
                  AI Confidence: {Math.round(extractedData.extraction_confidence * 100)}%
                </Tag>
                {extractedData.vendor_name && (
                  <Tag color="blue">Vendor: {extractedData.vendor_name}</Tag>
                )}
              </div>
            )}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="invoice_number" label={<span className="p5-label">Invoice Number</span>} rules={[{ required: true }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="client_vendor_id" label={<span className="p5-label">Vendor</span>} rules={[{ required: true }]}>
                <Select placeholder="Select vendor" showSearch optionFilterProp="children" size="large">
                  {vendors.map(v => (
                    <Option key={v.id} value={v.id}>{v.vendor?.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="invoice_date" label={<span className="p5-label">Invoice Date</span>} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="due_date" label={<span className="p5-label">Due Date</span>}>
                <DatePicker style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="total_amount" label={<span className="p5-label">Total Amount</span>} rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} prefix="$" style={{ width: '100%' }} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="purchase_order_id" label={<span className="p5-label">Link to PO</span>}>
            <Select placeholder="Select PO (optional)" allowClear showSearch optionFilterProp="children" size="large">
              {purchaseOrders.map(po => (
                <Option key={po.id} value={po.id}>{po.po_number}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label={<span className="p5-label">Notes</span>}>
            <TextArea rows={2} size="large" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button className="p5-btn p5-btn--ghost" onClick={() => {
              setUploadModalVisible(false);
              uploadForm.resetFields();
              setSelectedFile(null);
            }}>Cancel</Button>
            <Button className="p5-btn p5-btn--accent" htmlType="submit">Upload Invoice</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default Invoices;
